import { useState, useCallback, useMemo, useEffect } from 'react';
import * as Cesium from 'cesium';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ToolPanel from '../components/ToolPanel';
import TipsWindow from '../components/TipsWindow';
import { useTip } from '../hooks/useTips';
import {
  TYPOGRAPHY_STYLES,
  LAYER_STYLES,
} from '../styles/viewerStyles';

import config from "../../config";

const GOOGLE_PHOTOREALISTIC_TILESET_ID = config.googlePhotoRealisticTilesetID;
const GOOGLE_PHOTOREALISTIC_THUMB_URL = 'https://images.prismic.io/cesium/03877f30-234e-422f-9060-f48f1e2d75c8_screenshots_00000.png?auto=compress%2Cformat&w=1322';

function LayersTools({
  viewer,
  toggleBaseLayerPanel,
  imageryProviderViewModels,
  terrainProviderViewModels,
  DRAWER_WIDTH
}) {
  const { t } = useTranslation();
  const viewerState = viewer ? (viewer.__mirukuru || (viewer.__mirukuru = {})) : null;
  const terrainIndexFromViewer = typeof viewerState?.terrainIndex === 'number' ? viewerState.terrainIndex : undefined;
  const imageryIndexFromViewer = typeof viewerState?.imageryIndex === 'number' ? viewerState.imageryIndex : undefined;
  const buildingIndexFromViewer = typeof viewerState?.buildingIndex === 'number' ? viewerState.buildingIndex : undefined;
  const globalLayerActiveFromViewer = viewerState?.globalLayerActive === true;

  const [selectedImageryProvider, setSelectedImageryProvider] = useState(
    typeof imageryIndexFromViewer === 'number' ? imageryIndexFromViewer : 0
  );
  const [selectedTerrainProvider, setSelectedTerrainProvider] = useState(
    typeof terrainIndexFromViewer === 'number' ? terrainIndexFromViewer : 0
  );
  const [selectedBuildingProvider, setSelectedBuildingProvider] = useState(
    typeof buildingIndexFromViewer === 'number'
      ? buildingIndexFromViewer
      : (typeof window !== 'undefined' && window.selectedBuildingIndex !== undefined ? window.selectedBuildingIndex : 0)
  );
  const [isGlobalLayerActive, setIsGlobalLayerActive] = useState(globalLayerActiveFromViewer);

  useEffect(() => {
    if (typeof imageryIndexFromViewer === 'number' && imageryIndexFromViewer !== selectedImageryProvider) {
      setSelectedImageryProvider(imageryIndexFromViewer);
    }
  }, [imageryIndexFromViewer, selectedImageryProvider]);

  useEffect(() => {
    if (typeof terrainIndexFromViewer === 'number' && terrainIndexFromViewer !== selectedTerrainProvider) {
      setSelectedTerrainProvider(terrainIndexFromViewer);
    }
  }, [terrainIndexFromViewer, selectedTerrainProvider]);

  useEffect(() => {
    if (typeof buildingIndexFromViewer === 'number' && buildingIndexFromViewer !== selectedBuildingProvider) {
      setSelectedBuildingProvider(buildingIndexFromViewer);
    }
  }, [buildingIndexFromViewer, selectedBuildingProvider]);

  useEffect(() => {
    if (globalLayerActiveFromViewer !== isGlobalLayerActive) {
      setIsGlobalLayerActive(globalLayerActiveFromViewer);
    }
  }, [globalLayerActiveFromViewer, isGlobalLayerActive]);

  const bingImageryIndex = useMemo(() => {
    if (!Array.isArray(imageryProviderViewModels)) return -1;
    return imageryProviderViewModels.findIndex((model) =>
      (model?.name || '').toLowerCase().includes('bing maps aerial')
    );
  }, [imageryProviderViewModels]);

  const cesiumWorldTerrainIndex = useMemo(() => {
    if (!Array.isArray(terrainProviderViewModels)) return -1;
    return terrainProviderViewModels.findIndex((model) =>
      (model?.name || '').toLowerCase().includes('cesium world terrain')
    );
  }, [terrainProviderViewModels]);

  const buildingProviderViewModels = [
    { name: t('BuildingLayersNone') || 'None', assetId: null, iconUrl: null },
    { name: t('BuildingLayersOSM') || 'Cesium OSM Buildings', assetId: config.osmBuildingsTilsetID, iconUrl: 'http://i.imgur.com/pxh2Cpt.png' },
    { name: t('BuildingLayersJapan3D') || 'Japan 3D Building Data', assetId: config.japan3DBuildingsTilsetID, iconUrl: 'https://images.prismic.io/cesium/Zl4BBpm069VX1a1A_KyotoCity_Unreal.png?auto=compress%2Cformat&fit=crop&ar=16%3A9&crop=faces%2Ccenter&w=1322' },
  ];

  const switchImageryLayer = useCallback((providerIndex) => {
    if (!viewer || !imageryProviderViewModels || providerIndex < 0 || providerIndex >= imageryProviderViewModels.length) return;

    const selectedModel = imageryProviderViewModels[providerIndex];
    try {
      const creationFunction = selectedModel._creationCommand || selectedModel.creationFunction || selectedModel.command;
      if (typeof creationFunction !== 'function') return;
      let newProvider = creationFunction();
      const baseLayer = viewer.imageryLayers.get(0);
      if (baseLayer) viewer.imageryLayers.remove(baseLayer);
      if (newProvider && typeof newProvider.then === 'function') {
        newProvider.then((resolved) => {
          viewer.imageryLayers.addImageryProvider(resolved, 0);
          setSelectedImageryProvider(providerIndex);
          const state = viewer.__mirukuru || (viewer.__mirukuru = {});
          state.imageryIndex = providerIndex;
          state.globalLayerActive = false;
          setIsGlobalLayerActive(false);
        }).catch((error) => {
          console.warn('Failed to switch imagery layer:', error);
          if (baseLayer && !baseLayer.isDestroyed()) viewer.imageryLayers.add(baseLayer, 0);
        });
      } else {
        viewer.imageryLayers.addImageryProvider(newProvider, 0);
        setSelectedImageryProvider(providerIndex);
        const state = viewer.__mirukuru || (viewer.__mirukuru = {});
        state.imageryIndex = providerIndex;
        state.globalLayerActive = false;
        setIsGlobalLayerActive(false);
      }
    } catch (error) {
      console.warn('Failed to switch imagery layer:', error);
    }
  }, [viewer, imageryProviderViewModels]);

  const switchTerrainLayer = useCallback((providerIndex) => {
    if (!viewer || !terrainProviderViewModels || providerIndex < 0 || providerIndex >= terrainProviderViewModels.length) return;

    const selectedModel = terrainProviderViewModels[providerIndex];
    try {
      const creationFunction = selectedModel._creationCommand || selectedModel.creationFunction || selectedModel.command;
      if (typeof creationFunction !== 'function') return;
      const newProvider = creationFunction();
      const applyProvider = (provider) => {
        viewer.terrainProvider = provider;
        setSelectedTerrainProvider(providerIndex);
        const state = viewer.__mirukuru || (viewer.__mirukuru = {});
        state.terrainIndex = providerIndex;
        state.globalLayerActive = false;
        setIsGlobalLayerActive(false);
      };
      if (newProvider && typeof newProvider.then === 'function') {
        newProvider.then(applyProvider).catch((error) => {
          console.warn('Failed to switch terrain layer:', error);
        });
      } else {
        applyProvider(newProvider);
      }
    } catch (error) {
      console.warn('Failed to switch terrain layer:', error);
    }
  }, [viewer, terrainProviderViewModels]);

  const switchBuildingLayer = useCallback(async (providerIndex) => {
    if (!viewer || providerIndex < 0 || providerIndex >= buildingProviderViewModels.length) return;

    try {
      if (viewer.__buildingTileset) {
        try {
          viewer.scene.primitives.remove(viewer.__buildingTileset);
        } catch (removalError) {
          console.warn('Failed to remove previous building tileset:', removalError);
        }
        viewer.__buildingTileset = null;
      }

      const selectedModel = buildingProviderViewModels[providerIndex];
      if (selectedModel.assetId) {
        const ts = await Cesium.Cesium3DTileset.fromIonAssetId(selectedModel.assetId);
        const added = viewer.scene.primitives.add(ts);
        viewer.__buildingTileset = added;
      }

      setSelectedBuildingProvider(providerIndex);
      if (typeof window !== 'undefined') window.selectedBuildingIndex = providerIndex;
      const state = viewer.__mirukuru || (viewer.__mirukuru = {});
      state.buildingIndex = providerIndex;
    } catch (error) {
      console.warn('Failed to switch building layer:', error);
    }
  }, [viewer]);

  const deactivateGlobalLayer = useCallback(() => {
    if (!viewer) return;

    const existing = viewer.__globalPhotorealisticTileset;
    if (existing) {
      try {
        viewer.scene.primitives.remove(existing);
      } catch (error) {
        console.warn('Failed to remove global photorealistic tileset:', error);
      }
      viewer.__globalPhotorealisticTileset = null;
    }

    const state = viewer.__mirukuru || (viewer.__mirukuru = {});
    if (state.globalLayerActive) state.globalLayerActive = false;

    if (isGlobalLayerActive) setIsGlobalLayerActive(false);
  }, [viewer, isGlobalLayerActive]);

  const activateGlobalLayer = useCallback(async () => {
    if (!viewer || isGlobalLayerActive) return;

    try {
      if (viewer.__globalPhotorealisticTileset) {
        try {
          viewer.scene.primitives.remove(viewer.__globalPhotorealisticTileset);
        } catch (removalError) {
          console.warn('Failed to remove existing global tileset:', removalError);
        }
        viewer.__globalPhotorealisticTileset = null;
      }

      const tileset = await Cesium.Cesium3DTileset.fromIonAssetId(GOOGLE_PHOTOREALISTIC_TILESET_ID);
      const addedTileset = viewer.scene.primitives.add(tileset);
      viewer.__globalPhotorealisticTileset = addedTileset;

      viewer.imageryLayers?.removeAll?.();
      viewer.terrainProvider = new Cesium.EllipsoidTerrainProvider();
      const state = viewer.__mirukuru || (viewer.__mirukuru = {});
      state.terrainIndex = -1;
      state.imageryIndex = -1;
      state.globalLayerActive = true;

      await switchBuildingLayer(0);

      setSelectedImageryProvider(-1);
      setSelectedTerrainProvider(-1);
      setIsGlobalLayerActive(true);
    } catch (error) {
      console.warn('Failed to activate global layer:', error);
    }
  }, [viewer, isGlobalLayerActive, switchBuildingLayer]);

  const handleImageryLayerClick = useCallback(async (providerIndex) => {
    if (!viewer || !imageryProviderViewModels || providerIndex < 0 || providerIndex >= imageryProviderViewModels.length) return;

    if (isGlobalLayerActive) {
      deactivateGlobalLayer();

      const terrainIndex = cesiumWorldTerrainIndex >= 0
        ? cesiumWorldTerrainIndex
        : (terrainProviderViewModels?.length ? 0 : -1);
      if (terrainIndex >= 0) switchTerrainLayer(terrainIndex);

      await switchBuildingLayer(0);
    }

    switchImageryLayer(providerIndex);
  }, [viewer, imageryProviderViewModels, isGlobalLayerActive, deactivateGlobalLayer, cesiumWorldTerrainIndex, terrainProviderViewModels, switchTerrainLayer, switchBuildingLayer, switchImageryLayer]);

  const handleTerrainLayerClick = useCallback(async (providerIndex) => {
    if (!viewer || !terrainProviderViewModels || providerIndex < 0 || providerIndex >= terrainProviderViewModels.length) return;

    if (isGlobalLayerActive) {
      deactivateGlobalLayer();

      const imageryIndex = bingImageryIndex >= 0
        ? bingImageryIndex
        : (imageryProviderViewModels?.length ? 0 : -1);
      if (imageryIndex >= 0) switchImageryLayer(imageryIndex);

      await switchBuildingLayer(0);
    }

    switchTerrainLayer(providerIndex);
  }, [viewer, terrainProviderViewModels, isGlobalLayerActive, deactivateGlobalLayer, bingImageryIndex, imageryProviderViewModels, switchImageryLayer, switchBuildingLayer, switchTerrainLayer]);

  const handleBuildingLayerClick = useCallback(async (providerIndex) => {
    if (!viewer || providerIndex < 0 || providerIndex >= buildingProviderViewModels.length) return;

    if (isGlobalLayerActive) {
      deactivateGlobalLayer();

      const imageryIndex = bingImageryIndex >= 0
        ? bingImageryIndex
        : (imageryProviderViewModels?.length ? 0 : -1);
      if (imageryIndex >= 0) switchImageryLayer(imageryIndex);

      const terrainIndex = cesiumWorldTerrainIndex >= 0
        ? cesiumWorldTerrainIndex
        : (terrainProviderViewModels?.length ? 0 : -1);
      if (terrainIndex >= 0) switchTerrainLayer(terrainIndex);
    }

    await switchBuildingLayer(providerIndex);
  }, [viewer, buildingProviderViewModels, isGlobalLayerActive, deactivateGlobalLayer, bingImageryIndex, imageryProviderViewModels, switchImageryLayer, cesiumWorldTerrainIndex, terrainProviderViewModels, switchTerrainLayer, switchBuildingLayer]);

  const handleGlobalLayerClick = useCallback(async () => {
    if (isGlobalLayerActive) return;
    await activateGlobalLayer();
  }, [isGlobalLayerActive, activateGlobalLayer]);

  // Tips integration - component is only mounted when panel is open
  const { isOpen: isTipOpen, title: tipTitle, markdownBody: tipBody, close: closeTip } = useTip('layers', true);

  return (
    <>
    <TipsWindow
      isOpen={isTipOpen}
      title={tipTitle}
      markdownBody={tipBody}
      onClose={({ doNotShowAgain }) => closeTip(doNotShowAgain)}
    />
    <ToolPanel
      title={t('BaseLayers') || 'Base Layers'}
      onClose={toggleBaseLayerPanel}
      width={DRAWER_WIDTH}
      maxHeight="calc(100vh - 140px)"
    >
      {/* Imagery Section */}
      <Box sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ ...TYPOGRAPHY_STYLES.subtitle, mb: 2, px: 0.5 }}>
          {t('ImageryLayers') || 'Imagery Layers'}
        </Typography>
        <Box sx={LAYER_STYLES.grid}>
          {imageryProviderViewModels?.map((model, index) => (
            <Box key={index} sx={LAYER_STYLES.item} onClick={() => handleImageryLayerClick(index)}>
              <Box sx={LAYER_STYLES.thumbnail(selectedImageryProvider === index, model.iconUrl, '#444')}>
                {!model.iconUrl && (
                  <Box sx={{ ...LAYER_STYLES.placeholder, color: '#888' }}>MAP</Box>
                )}
              </Box>
              <Typography sx={TYPOGRAPHY_STYLES.label}>{model.name}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Terrain Section */}
      <Typography variant="subtitle2" sx={{ ...TYPOGRAPHY_STYLES.subtitle, mb: 2, px: 0.5 }}>
        {t('TerrainLayers') || 'Terrain Layers'}
      </Typography>
      <Box sx={LAYER_STYLES.grid}>
        {terrainProviderViewModels?.map((model, index) => (
          <Box key={index} sx={LAYER_STYLES.item} onClick={() => handleTerrainLayerClick(index)}>
            <Box sx={LAYER_STYLES.thumbnail(selectedTerrainProvider === index, model.iconUrl, '#666')}>
              {!model.iconUrl && (
                <Box sx={{ ...LAYER_STYLES.placeholder, color: '#aaa' }}>TERRAIN</Box>
              )}
            </Box>
            <Typography sx={TYPOGRAPHY_STYLES.label}>{model.name}</Typography>
          </Box>
        ))}
      </Box>

      {/* Building Section */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ ...TYPOGRAPHY_STYLES.subtitle, mb: 2, px: 0.5 }}>
          {t('BuildingLayers') || 'Building Layers'}
        </Typography>
        <Box sx={LAYER_STYLES.grid}>
          {buildingProviderViewModels.map((model, index) => (
            <Box key={index} sx={LAYER_STYLES.item} onClick={() => handleBuildingLayerClick(index)}>
              <Box sx={LAYER_STYLES.thumbnail(selectedBuildingProvider === index, model.iconUrl, '#555')}>
                {!model.iconUrl && (
                  <Box sx={{ ...LAYER_STYLES.placeholder, color: '#aaa' }}>
                    {model.assetId ? 'BUILDINGS' : (t('BuildingLayersNone') || 'NONE')}
                  </Box>
                )}
              </Box>
              <Typography sx={TYPOGRAPHY_STYLES.label}>{model.name}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Global Layer Section */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ ...TYPOGRAPHY_STYLES.subtitle, mb: 2, px: 0.5 }}>
          {t('GlobalLayer')}
        </Typography>
        <Box sx={LAYER_STYLES.grid}>
          <Box
            sx={{
              ...LAYER_STYLES.item,
              cursor: isGlobalLayerActive ? 'default' : 'pointer',
              opacity: viewer ? 1 : 0.6
            }}
            onClick={() => handleGlobalLayerClick()}
          >
            <Box sx={LAYER_STYLES.thumbnail(isGlobalLayerActive, null, '#666')}>
              <Box
                component="img"
                src={GOOGLE_PHOTOREALISTIC_THUMB_URL}
                alt={t('GlobalLayerGoogleTiles')}
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'block',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  transform: 'scale(1.1)',
                  transformOrigin: 'center',
                  pointerEvents: 'none'
                }}
              />
            </Box>
            <Typography sx={TYPOGRAPHY_STYLES.label}>
              {t('GlobalLayerGoogleTiles')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </ToolPanel>
    </>
  );
}

LayersTools.propTypes = {
  viewer: PropTypes.object,
  toggleBaseLayerPanel: PropTypes.func.isRequired,
  imageryProviderViewModels: PropTypes.array,
  terrainProviderViewModels: PropTypes.array,
  DRAWER_WIDTH: PropTypes.number.isRequired,
};

export default LayersTools;
